import { useAuthenticator } from "@aws-amplify/ui-react";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import type { TAccountType } from "store/types";
import type { Tracker, TrackerEvent, TrackerEventData } from "types";

const devLogger = (...args) => {
    if (import.meta.env.DEV) {
        console.log(...args);
    }
};
// Context for tracking events using injected Tracker
const TrackerContext = createContext<{
    trackEvent: <T extends TrackerEvent>(event: T, data: TrackerEventData<T>) => void;
}>({
    trackEvent: () => {
        devLogger("[tracker] No tracker available");
    },
});

export const TrackerProvider = ({ children, tracker }: { children: ReactNode; tracker?: Tracker | null }) => {
    const { user } = useAuthenticator((context) => [context.user]);
    const { companyInfos } = useCompanySettings();

    const companyName = companyInfos?.name;

    useEffect(() => {
        if (!user) {
            devLogger("[tracker] No user");

            if (tracker) {
                // If tracker is available but user is null -> user logged out
                // Unregister session
                tracker.unregisterSession();
            }

            return;
        }

        if (!tracker) {
            devLogger("[tracker] No tracker available");
            return;
        }

        if (
            !user.username ||
            !user.attributes ||
            !user.attributes.name ||
            !user.attributes.email ||
            !user.attributes["custom:companyId"]
        ) {
            devLogger("[tracker] Invalid user", user);
            return;
        }

        if (!companyName) {
            devLogger("[tracker] No company name");
            return;
        }

        const accountType = user.attributes["custom:accountType"];
        if (accountType !== "publisher" && accountType !== "beneficiary" && accountType !== "admin") {
            devLogger("[tracker] Invalid account type", accountType);
            return;
        }

        devLogger("[tracker] Register session", user);

        tracker.registerSession({
            username: user.username,
            name: user.attributes.name,
            email: user.attributes.email,
            companyId: user.attributes["custom:companyId"],
            companyName,
            accountType: user.attributes["custom:accountType"] as TAccountType | "admin",
        });
    }, [user, companyName, tracker]);

    const trackEvent = useCallback(
        <T extends TrackerEvent>(event: T, data: TrackerEventData<T>) => {
            if (!tracker) {
                devLogger("[tracker](trackEvent) No tracker available");
                return;
            }

            tracker.trackEvent(event, data);
        },
        [tracker],
    );

    const contextValue = useMemo(() => ({ trackEvent }), [trackEvent]);

    return <TrackerContext.Provider value={contextValue}>{children}</TrackerContext.Provider>;
};

export const useTracker = () => {
    const context = useContext(TrackerContext);

    if (!context) {
        throw new Error("useTracker must be used within a TrackerProvider");
    }

    return context;
};
